import { Stack } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MiddleFormCard from "../../components/cards/MiddleFormCard";
import { PasswordInput } from "../../components/inputs";
import SubmitButton from "../../components/SubmitButton";
import { useApi } from "../../contexts/ApiContext";
import { useApp } from "../../contexts/AppContext";
import { useAuth } from "../../contexts/AuthContext";
import { useError } from "../../contexts/ErrorContext";
import { newPasswordSchema } from "./types";

type ErrorType = { [k: string]: string[] | undefined };
const NewPassword = () => {
  const { confirmSignIn } = useAuth();

  const [email, setEmail] = useState("");
  const [formErrors, setFormErrors] = useState<ErrorType>({});
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const [validateOnChange, setValidateOnChange] = useState<boolean>(false);
  const { showError, showSuccess } = useError();
  const navigate = useNavigate();

  const { translate } = useApp();
  const authTranslations = useMemo(() => translate("auth"), [translate]);
  const { setSubmitting } = useApi();

  const location = useLocation();
  const { username } = location.state || {};

  const validate = () => {
    const result = newPasswordSchema.safeParse({
      newPassword,
      newPasswordConfirm,
    });
    if (!result.success) {
      const errors = result.error.flatten().fieldErrors;
      console.info("result.error", errors);
      setFormErrors(errors);

      if (errors.newPassword?.length ?? 0 > 1) {
        showError(
          (errors.newPassword ?? []).map((e: string) => translate("auth", e)),
          10000
        );
      }
      return false;
    } else {
      setFormErrors({});
    }
    return true;
  };

  const handleSubmit = async () => {
    setValidateOnChange(true);
    if (!validate()) {
      return;
    }
    try {
      setSubmitting(true);
      await confirmSignIn(newPassword);
      showSuccess(authTranslations.new_password.messages.password_updated);
      window.location.href = "/dashboard";
    } catch (error: any) {
      console.error("Error updating password", error);
      showError(
        `${authTranslations.errors.error_resetting_password}: ${error?.message}`
      );
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (validateOnChange) {
      validate();
    }
  }, [email, newPassword, newPasswordConfirm]);

  useEffect(() => {
    console.info("new password user", username);
    if (username) {
      setEmail(username);
    }
  }, [username]);

  return (
    <MiddleFormCard
      width="600px"
      subtitle={authTranslations.new_password.subtitle}
      title={authTranslations.new_password.title}
      onSubmit={() => {}}
    >
      <Stack gap={1}>
        <PasswordInput
          label={authTranslations.new_password.fields.new_password}
          value={newPassword}
          onChange={(value: string) => setNewPassword(value)}
          helperText={translate("auth", formErrors.newPassword?.[0])}
          error={!!formErrors.newPassword}
        />
        <PasswordInput
          error={!!formErrors.newPasswordConfirm}
          helperText={translate("auth", formErrors.newPasswordConfirm?.[0])}
          label={authTranslations.new_password.fields.confirm_password}
          value={newPasswordConfirm}
          onChange={(value: number | string) =>
            setNewPasswordConfirm(value as string)
          }
        />
        <Stack justifyContent={"flex-end"} gap={1} direction={"row"}>
          <SubmitButton
            fullWidth={false}
            color={"blackButton"}
            onClick={handleSubmit}
          >
            {authTranslations.new_password.buttons.submit}
          </SubmitButton>
        </Stack>
      </Stack>
    </MiddleFormCard>
  );
};

export default NewPassword;
